import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from 'services/api';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Textarea } from 'components/ui/textarea';
import { 
  ArrowLeft, Heart, Smile, Meh, Frown, AlertTriangle, 
  TrendingDown, TrendingUp, Minus
} from 'lucide-react';
import { toast } from 'sonner';

const STRESS_LEVELS = [
  { level: 1, label: 'Great', icon: Smile, color: 'bg-green-500 hover:bg-green-600', textColor: 'text-green-600' },
  { level: 2, label: 'Good', icon: Smile, color: 'bg-lime-500 hover:bg-lime-600', textColor: 'text-lime-600' },
  { level: 3, label: 'Okay', icon: Meh, color: 'bg-amber-500 hover:bg-amber-600', textColor: 'text-amber-600' },
  { level: 4, label: 'Stressed', icon: Frown, color: 'bg-orange-500 hover:bg-orange-600', textColor: 'text-orange-600' },
  { level: 5, label: 'Overwhelmed', icon: AlertTriangle, color: 'bg-rose-500 hover:bg-rose-600', textColor: 'text-rose-600' },
];

export default function StressCheckin() {
  const navigate = useNavigate();
  const [checkins, setCheckins] = useState([]);
  const [trend, setTrend] = useState({ average: null, trend: 'neutral', count: 0 });
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [supportMessage, setSupportMessage] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [checkinsRes, trendRes] = await Promise.all([
        api.get('/stress-checkins'),
        api.get('/stress-checkins/trend')
      ]);
      setCheckins(checkinsRes.data.checkins || []);
      setTrend(trendRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedLevel) {
      toast.error('Please select how you\'re feeling');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/stress-checkins', {
        stress_level: selectedLevel,
        notes: notes || null
      });

      toast.success('Check-in recorded!');
      setSupportMessage(response.data.support_message);
      setSelectedLevel(null);
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to save check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const getTrendIcon = () => {
    switch (trend.trend) {
      case 'improving':
        return <TrendingDown className="w-5 h-5 text-green-600" />;
      case 'worsening':
        return <TrendingUp className="w-5 h-5 text-rose-600" />;
      default:
        return <Minus className="w-5 h-5 text-slate-400" />;
    }
  };

  const getTrendText = () => {
    switch (trend.trend) {
      case 'improving':
        return 'Your stress levels are improving!';
      case 'worsening':
        return 'Your stress levels are rising. Consider self-care.';
      default:
        return 'Your stress levels are stable.';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse-soft">
          <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-teal-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="stress-checkin">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Stress Check-in</h1>
            <p className="text-xs sm:text-sm text-slate-500">How are you feeling today?</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Support Message */}
        {supportMessage && (
          <Card className="bg-teal-50 border-teal-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Heart className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-teal-800">We're Here For You</p>
                  <p className="text-sm text-teal-700 mt-1">{supportMessage}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 text-teal-600"
                onClick={() => setSupportMessage(null)}
              >
                Dismiss
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Check-in Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-center font-['Manrope']">How are you feeling right now?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
              {STRESS_LEVELS.map((level) => (
                <button
                  key={level.level}
                  onClick={() => setSelectedLevel(level.level)}
                  className={`flex flex-col items-center p-2 sm:p-3 rounded-xl transition-all ${
                    selectedLevel === level.level
                      ? `${level.color} text-white scale-105 shadow-lg`
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                  }`}
                  data-testid={`stress-level-${level.level}`}
                >
                  <level.icon className="w-6 h-6 sm:w-8 sm:h-8 mb-0.5 sm:mb-1" />
                  <span className="text-[10px] sm:text-xs font-medium leading-tight">{level.label}</span>
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                What's on your mind? (Optional)
              </label>
              <Textarea
                placeholder="Share your thoughts or what's contributing to how you feel..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[100px]"
                data-testid="stress-notes"
              />
            </div>

            <Button
              onClick={handleSubmit}
              className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white rounded-lg"
              disabled={!selectedLevel || submitting}
              data-testid="submit-checkin-btn"
            >
              {submitting ? 'Saving...' : 'Save Check-in'}
            </Button>
          </CardContent>
        </Card>

        {/* Trend Card */}
        {trend.count > 0 && (
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-slate-500">7-Day Average</p>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl sm:text-3xl font-bold text-slate-900">{trend.average?.toFixed(1)}</span>
                    <span className="text-slate-400">/5</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-full">
                  {getTrendIcon()}
                  <span className="text-sm font-medium text-slate-700 capitalize">{trend.trend}</span>
                </div>
              </div>
              <p className="text-sm text-slate-600">{getTrendText()}</p>
              <div className="mt-4 flex gap-1">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div
                    key={level}
                    className={`flex-1 h-2 rounded-full ${
                      level <= Math.round(trend.average || 0)
                        ? STRESS_LEVELS[level - 1].color.replace('hover:bg-', '').split(' ')[0]
                        : 'bg-slate-200'
                    }`}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* History */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope']">Recent Check-ins</CardTitle>
          </CardHeader>
          <CardContent>
            {checkins.length === 0 ? (
              <div className="text-center py-8">
                <Heart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No check-ins yet. Start tracking your wellbeing!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {checkins.slice(0, 10).map((checkin) => {
                  const levelInfo = STRESS_LEVELS[checkin.stress_level - 1];
                  return (
                    <div
                      key={checkin.checkin_id}
                      className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl"
                    >
                      <div className={`w-10 h-10 rounded-full ${levelInfo.color.split(' ')[0]} flex items-center justify-center flex-shrink-0`}>
                        <levelInfo.icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${levelInfo.textColor}`}>{levelInfo.label}</span>
                          <span className="text-xs text-slate-400">
                            {new Date(checkin.created_at).toLocaleString()}
                          </span>
                        </div>
                        {checkin.notes && (
                          <p className="text-sm text-slate-600 mt-1">{checkin.notes}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="bg-slate-900 text-white">
          <CardContent className="p-4 sm:p-6">
            <h3 className="font-semibold text-lg mb-3 font-['Manrope']">Caregiver Self-Care Tips</h3>
            <ul className="space-y-2 text-slate-300 text-sm">
              <li>• Take short breaks, even just 5 minutes of deep breathing</li>
              <li>• Stay connected with friends and family for support</li>
              <li>• Set realistic expectations for yourself</li>
              <li>• Don't hesitate to ask for help when needed</li>
              <li>• Remember: caring for yourself is part of caring for others</li>
            </ul>
            <Link to="/care-guide">
              <Button className="mt-4 bg-teal-500 hover:bg-teal-600 text-white w-full">
                Talk to AI Care Guide
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
